import { useCallback } from 'react';
import { FilterToolbarEmbedded } from 'src/components/FilterToolbarEmbedded';
import {
  commissionMandatoryFiltersToShow,
  useCommissionFilters,
} from 'src/components/MainFilterBar/useCommissionFilters';
import {
  costOfGoodsSoldReportMandatoryFiltersToShow,
  useCostOfGoodsSoldReportFilters,
} from 'src/components/MainFilterBar/useCostOfGoodsSoldReportFilter';
import { listingsMandatoryFiltersToShow } from 'src/components/MainFilterBar/useListingFilters';
import { useListingReportFilters } from 'src/components/MainFilterBar/useListingReportFilters';
import { purchaseMandatoryFiltersToShow } from 'src/components/MainFilterBar/usePurchaseFilters';
import {
  purchaseOrderLineItemMandatoryFiltersToShow,
  usePurchaseOrderLineItemReportFilters,
} from 'src/components/MainFilterBar/usePurchaseOrderLineItemReportFilters';
import {
  soldTicketReportMandatoryFiltersToShow,
  usePurchaseReportFilters,
} from 'src/components/MainFilterBar/usePurchaseReportFilters';
import {
  saleCostAdjustmentsReportMandatoryFiltersToShow,
  useSaleCostAdjustmentsReportFilters,
} from 'src/components/MainFilterBar/useSaleCostAdjustmentsReportFilter';
import {
  saleLineItemReportMandatoryFiltersToShow,
  useSaleLineItemReportFilters,
} from 'src/components/MainFilterBar/useSaleLineItemReportFilters';
import {
  saleReportMandatoryFiltersToShow,
  useSaleReportFilters,
} from 'src/components/MainFilterBar/useSaleReportFilters';
import { useSoldTicketReportFilters } from 'src/components/MainFilterBar/useSoldTicketReportFilters';
import {
  ticketGroupReportMandatoryFiltersToShow,
  useTicketGroupReportFilters,
} from 'src/components/MainFilterBar/useTicketGroupReportFilters';
import { useAppContext } from 'src/contexts/AppContext';
import { CatalogDataContextV2Provider } from 'src/contexts/CatalogDataContext/CatalogDataContextV2';
import { Stack } from 'src/core/ui';
import {
  DefaultCommissionQuery,
  DefaultReportV2CostOfGoodsSoldQuery,
  DefaultReportV2ListingQuery,
  DefaultReportV2PurchaseOrderLineItemQuery,
  DefaultReportV2PurchaseQuery,
  DefaultReportV2SaleCostAdjustmentsQuery,
  DefaultReportV2SaleLineItemQuery,
  DefaultReportV2SaleQuery,
  DefaultReportV2SoldTicketQuery,
  DefaultReportV2TicketGroupQuery,
} from 'src/utils/eventQueryUtils';
import { transformData } from 'src/utils/eventWithDataUtils';
import {
  getCatalogCountForReport,
  getEventsWithNonZeroCountsReporting,
  ReportTypesV2,
} from 'src/utils/reportsUtils';
import {
  ActionOutboxEntityType,
  EventWithData,
  ReportV2Client,
  UIReportCommissionQuery,
  UIReportV2CostOfGoodsSoldQuery,
  UIReportV2ListingQuery,
  UIReportV2PurchaseOrderLineItemQuery,
  UIReportV2PurchaseQuery,
  UIReportV2SaleLineItemQuery,
  UIReportV2SaleQuery,
  UIReportV2SaleTotalCostAdjustmentsQuery,
  UIReportV2SoldTicketQuery,
  UIReportV2TicketGroupQuery,
} from 'src/WebApiController';

import {
  ReportFilterInputProps,
  useReportBuilderFilterInputV2,
} from './useReportBuilderFilterInputV2';

export function ReportBuilderFilterInputV2({
  reportType,
  direction,
  ...filterInputProps
}: {
  reportType: ReportTypesV2;
} & ReportFilterInputProps) {
  const reportTypeForCatalog =
    reportType === ReportTypesV2.InventoryV2 ||
    reportType === ReportTypesV2.SaleV2 ||
    reportType === ReportTypesV2.SoldTicket ||
    reportType === ReportTypesV2.SaleLineItem ||
    reportType === ReportTypesV2.TicketGroup
      ? reportType
      : undefined;

  const { activeAccountWebClientConfig } = useAppContext();

  const getCatalogCountForReportWrapper = useCallback(
    (filterQuery: unknown) => {
      if (!reportTypeForCatalog) {
        return Promise.resolve({} as never);
      }
      const client = new ReportV2Client(activeAccountWebClientConfig);
      return getCatalogCountForReport(
        client,
        filterQuery,
        reportTypeForCatalog
      );
    },
    [activeAccountWebClientConfig, reportTypeForCatalog]
  );

  const getEventsWithNonZeroCountsReportingWrapper = useCallback(
    (
      _entityType: ActionOutboxEntityType,
      data?: EventWithData[] | null,
      requireEventNotNull?: boolean
    ) => {
      if (!reportTypeForCatalog) {
        return [];
      }
      return getEventsWithNonZeroCountsReporting(
        data,
        requireEventNotNull,
        reportTypeForCatalog
      );
    },
    [reportTypeForCatalog]
  );

  return (
    <CatalogDataContextV2Provider
      queryKey={`getCatalogForReport${reportTypeForCatalog}`}
      entityType={
        reportTypeForCatalog === ReportTypesV2.InventoryV2
          ? ActionOutboxEntityType.Listing
          : reportTypeForCatalog === ReportTypesV2.TicketGroup
            ? ActionOutboxEntityType.Purchase
            : ActionOutboxEntityType.Sale
      }
      getCatalogCountsCustom={getCatalogCountForReportWrapper}
      getEventsWithNonZeroCountsCustom={
        getEventsWithNonZeroCountsReportingWrapper
      }
      transformEventData={transformData}
      disabled={!reportTypeForCatalog}
    >
      <Stack
        direction={direction}
        alignItems={direction === 'column' ? 'start' : 'center'}
        gap="l"
        style={{
          flexGrow: '1',
        }}
      >
        {reportType === ReportTypesV2.InventoryV2 ? (
          <ListingFilterInput direction={direction} {...filterInputProps} />
        ) : reportType === ReportTypesV2.Commission ? (
          <CommissionFilterInput
            direction={direction}
            performerVenueFilter={undefined}
            {...filterInputProps}
          />
        ) : reportType === ReportTypesV2.Purchase ? (
          <PurchaseFilterInput
            direction={direction}
            performerVenueFilter={undefined}
            {...filterInputProps}
          />
        ) : reportType === ReportTypesV2.SoldTicket ? (
          <SoldTicketFilterInput
            direction={direction}
            performerVenueFilter={undefined}
            {...filterInputProps}
          />
        ) : reportType === ReportTypesV2.SaleLineItem ? (
          <SaleLineItemFilterInput
            direction={direction}
            performerVenueFilter={undefined}
            {...filterInputProps}
          />
        ) : reportType === ReportTypesV2.PurchaseOrderLineItem ? (
          <PurchaseLineItemFilterInput
            direction={direction}
            performerVenueFilter={undefined}
            {...filterInputProps}
          />
        ) : reportType === ReportTypesV2.CostOfGoodsSold ? (
          <CostOfGoodsSoldFilterInput
            direction={direction}
            performerVenueFilter={undefined}
            {...filterInputProps}
          />
        ) : reportType === ReportTypesV2.SaleCostAdjustments ? (
          <SaleCostAdjustmentsFilterInput
            direction={direction}
            performerVenueFilter={undefined}
            {...filterInputProps}
          />
        ) : reportType === ReportTypesV2.TicketGroup ? (
          <TicketGroupFilterInput
            direction={direction}
            performerVenueFilter={undefined}
            {...filterInputProps}
          />
        ) : (
          <SaleFilterInput direction={direction} {...filterInputProps} />
        )}
      </Stack>
    </CatalogDataContextV2Provider>
  );
}

const ListingFilterInput = (props: ReportFilterInputProps) => {
  const { filters } = useListingReportFilters({ isHorizontalLayout: true });

  const { filterToolbarProps } =
    useReportBuilderFilterInputV2<UIReportV2ListingQuery>({
      ...props,
      filters,
      mandatoryFiltersToShow: listingsMandatoryFiltersToShow,
      defaultQuery: DefaultReportV2ListingQuery,
    });

  return <FilterToolbarEmbedded {...filterToolbarProps} />;
};

const SaleFilterInput = (props: ReportFilterInputProps) => {
  const { filters } = useSaleReportFilters({ isHorizontalLayout: true });

  const { filterToolbarProps } =
    useReportBuilderFilterInputV2<UIReportV2SaleQuery>({
      ...props,
      filters,
      mandatoryFiltersToShow: saleReportMandatoryFiltersToShow,
      defaultQuery: DefaultReportV2SaleQuery,
    });

  return <FilterToolbarEmbedded {...filterToolbarProps} />;
};

const CommissionFilterInput = (props: ReportFilterInputProps) => {
  const { filters } = useCommissionFilters();

  const { filterToolbarProps } =
    useReportBuilderFilterInputV2<UIReportCommissionQuery>({
      ...props,
      filters,
      mandatoryFiltersToShow: commissionMandatoryFiltersToShow,
      defaultQuery: DefaultCommissionQuery,
    });

  return <FilterToolbarEmbedded {...filterToolbarProps} />;
};

const SaleLineItemFilterInput = (props: ReportFilterInputProps) => {
  const { filters } = useSaleLineItemReportFilters({
    isHorizontalLayout: true,
  });

  const { filterToolbarProps } =
    useReportBuilderFilterInputV2<UIReportV2SaleLineItemQuery>({
      ...props,
      filters,
      mandatoryFiltersToShow: saleLineItemReportMandatoryFiltersToShow,
      defaultQuery: DefaultReportV2SaleLineItemQuery,
    });

  return <FilterToolbarEmbedded {...filterToolbarProps} />;
};

const PurchaseFilterInput = (props: ReportFilterInputProps) => {
  const { filters } = usePurchaseReportFilters({ isHorizontalLayout: true });

  const { filterToolbarProps } =
    useReportBuilderFilterInputV2<UIReportV2PurchaseQuery>({
      ...props,
      filters,
      mandatoryFiltersToShow: purchaseMandatoryFiltersToShow,
      defaultQuery: DefaultReportV2PurchaseQuery,
    });

  return <FilterToolbarEmbedded {...filterToolbarProps} />;
};

const PurchaseLineItemFilterInput = (props: ReportFilterInputProps) => {
  const { filters } = usePurchaseOrderLineItemReportFilters({
    isHorizontalLayout: true,
  });

  const { filterToolbarProps } =
    useReportBuilderFilterInputV2<UIReportV2PurchaseOrderLineItemQuery>({
      ...props,
      filters,
      mandatoryFiltersToShow: purchaseOrderLineItemMandatoryFiltersToShow,
      defaultQuery: DefaultReportV2PurchaseOrderLineItemQuery,
    });

  return <FilterToolbarEmbedded {...filterToolbarProps} />;
};

const CostOfGoodsSoldFilterInput = (props: ReportFilterInputProps) => {
  const { filters } = useCostOfGoodsSoldReportFilters({
    isHorizontalLayout: true,
  });

  const { filterToolbarProps } =
    useReportBuilderFilterInputV2<UIReportV2CostOfGoodsSoldQuery>({
      ...props,
      filters,
      mandatoryFiltersToShow: costOfGoodsSoldReportMandatoryFiltersToShow,
      defaultQuery: DefaultReportV2CostOfGoodsSoldQuery,
    });

  return <FilterToolbarEmbedded {...filterToolbarProps} />;
};

const SaleCostAdjustmentsFilterInput = (props: ReportFilterInputProps) => {
  const { filters } = useSaleCostAdjustmentsReportFilters({
    isHorizontalLayout: true,
  });

  const { filterToolbarProps } =
    useReportBuilderFilterInputV2<UIReportV2SaleTotalCostAdjustmentsQuery>({
      ...props,
      filters,
      mandatoryFiltersToShow: saleCostAdjustmentsReportMandatoryFiltersToShow,
      defaultQuery: DefaultReportV2SaleCostAdjustmentsQuery,
    });

  return <FilterToolbarEmbedded {...filterToolbarProps} />;
};

const SoldTicketFilterInput = (props: ReportFilterInputProps) => {
  const { filters } = useSoldTicketReportFilters({});

  const { filterToolbarProps } =
    useReportBuilderFilterInputV2<UIReportV2SoldTicketQuery>({
      ...props,
      filters,
      mandatoryFiltersToShow: soldTicketReportMandatoryFiltersToShow,
      defaultQuery: DefaultReportV2SoldTicketQuery,
    });

  return <FilterToolbarEmbedded {...filterToolbarProps} />;
};

const TicketGroupFilterInput = (props: ReportFilterInputProps) => {
  const { filters } = useTicketGroupReportFilters({ isHorizontalLayout: true });

  const { filterToolbarProps } =
    useReportBuilderFilterInputV2<UIReportV2TicketGroupQuery>({
      ...props,
      filters,
      mandatoryFiltersToShow: ticketGroupReportMandatoryFiltersToShow,
      defaultQuery: DefaultReportV2TicketGroupQuery,
    });

  return <FilterToolbarEmbedded {...filterToolbarProps} />;
};
